import type { LocalHotelBooking, LocalHotelCheckout } from '@/types/StandaloneHotel'

// Local orders for the hotel booking page (PHP api/standalone_hotel): from the guest step on the booking
// no longer goes through the hotel search service.

const apiBase = (): string => (import.meta as any).env?.VITE_API_URL ?? ''

// Raw fetch so error bodies (422/409 with a message) reach the page and the
// X-Requested-With header the checkout guard expects is always sent.
async function call<T>(route: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${apiBase()}${route}`, {
    credentials: 'include',
    ...init,
    headers: { 'X-Requested-With': 'XMLHttpRequest', ...(init.headers || {}) }
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err: any = new Error(data?.error || `Request failed (${res.status})`)
    err.status = res.status
    err.data = data
    throw err
  }
  return data as T
}

export interface LocalCheckoutFields {
  checkout_token: string
  hotel_id: string
  hotel_name: string
  hotel_address: string
  room_code: string
  room_name: string
  option_code: string
  rate_plan_code: string
  rate_plan_name: string
  check_in: string
  check_out: string
  rooms: number
  adults: number
  children: number
  child_ages: string
  // JSON: [{ adults, childrenAges, onLapInfants }], one per room in room order
  pax_groups: string
  lead_given_name: string
  lead_family_name: string
  contact_email: string
  contact_phone: string
  // JSON: [{ given_name, family_name }]
  guests: string
  special_request: string
  amount: string
  // Currency of amount (the core-service quote)
  currency_code: string
  // Currency the page shows; PHP rejects a quote in any other
  pageCurrency: string
  intent_token: string
  // JSON: hotel, room, rate and policies as shown
  snapshot: string
}

class StandaloneHotelOrderService {
  private searchEnabledRequest: Promise<boolean> | null = null
  private searchEnabledValue: boolean | null = null

  /** Home page Hotels tab switch (ltw_setting_ext home_hotel_search_enabled); unreadable means off. Asked once per page. */
  searchEnabled(): Promise<boolean> {
    this.searchEnabledRequest ??= call<{ enabled: number }>('api/standalone_hotel/status').then(
      (r) => (this.searchEnabledValue = Number(r?.enabled) === 1),
      () => (this.searchEnabledValue = false)
    )
    return this.searchEnabledRequest
  }

  /** The switch if already read (null = not yet), so a tab bar mounted later shows it on its first render. */
  searchEnabledNow(): boolean | null {
    return this.searchEnabledValue
  }

  /** Creates (or refreshes for the same checkout token) the unpaid order the payment dialog pays. */
  checkout(fields: LocalCheckoutFields): Promise<LocalHotelCheckout> {
    const body = new URLSearchParams()
    Object.entries(fields).forEach(([k, v]) => body.set(k, String(v)))
    return call<LocalHotelCheckout>('api/standalone_hotel/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body
    })
  }

  booking(orderId: number): Promise<LocalHotelBooking> {
    return call<LocalHotelBooking>(`api/standalone_hotel/booking&order_id=${orderId}`)
  }
}

export default new StandaloneHotelOrderService()
